"use client"

import type { ComponentType, SVGProps } from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import {
	IconArrowRightOutline18 as ArrowRight,
	IconCheckOutline18 as Check,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import { Badge } from "./badge"

export interface OptionCard<T extends string> {
	value: T
	label: string
	Icon: ComponentType<SVGProps<SVGSVGElement>>
	/** Present on any option switches the group to the taller stacked layout. */
	description?: string
	badge?: string
}

interface Props<T extends string> {
	name: string
	value: T
	options: readonly OptionCard<T>[]
	onValueChange: (value: T) => void
	className?: string
}

const cardShell = cn(
	"flex flex-col rounded-[10px] px-3.5 py-3 text-left outline-none",
	"border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-transparent",
	"focus-visible:ring-1 focus-visible:ring-blue-500",
)

export interface ActionCardProps {
	Icon: ComponentType<SVGProps<SVGSVGElement>>
	title: string
	description: string
	/** Omit on a card that can never reach a done state; it then has no slot. */
	done?: boolean
	doneLabel?: string
	onClick: () => void
	/** Off in the dashboard, which keeps the default arrow on its own controls. */
	showPointer?: boolean
	className?: string
}

/**
 * A one-shot card. Unlike OptionCards it holds no selection, so clicking it
 * performs an action rather than picking a value.
 */
export function ActionCard({
	Icon,
	title,
	description,
	done,
	doneLabel = "Added",
	onClick,
	showPointer = true,
	className,
}: ActionCardProps) {
	return (
		<button
			type="button"
			disabled={done === true}
			onClick={onClick}
			aria-label={done ? `${title}, ${doneLabel}` : title}
			className={cn(
				cardShell,
				showPointer && !done ? "cursor-pointer" : "cursor-default",
				done ? "opacity-60" : "hover:bg-gray-100 dark:hover:bg-gray-800",
				className,
			)}
		>
			<span className="flex items-center gap-2">
				<Icon className="h-4 w-4 flex-none transform-gpu" strokeWidth={1.5} />
				<span className="min-w-0 flex-1 text-xs font-medium text-primary">
					{title}
				</span>
				{/* Reserved in both states so the card keeps its geometry. */}
				{done !== undefined ? (
					<span className="flex h-4 w-10 flex-none items-center justify-end gap-1">
						{done ? (
							<>
								<Check className="h-3 w-3 flex-none text-tertiary" />
								<span className="text-[11px] text-tertiary">{doneLabel}</span>
							</>
						) : null}
					</span>
				) : null}
			</span>
			<span
				title={description}
				className="mt-1 w-full truncate text-[11.5px] leading-relaxed text-tertiary"
			>
				{description}
			</span>
		</button>
	)
}

export interface StarterCardProps {
	Icon: ComponentType<SVGProps<SVGSVGElement>>
	title: string
	description: string
	actionLabel?: string
	onClick: () => void
	disabled?: boolean
	/** apps/app passes false; the marketing site keeps the pointer. */
	showPointer?: boolean
	className?: string
}

/**
 * The stacked sibling of ActionCard, for offering a few ways into a feature.
 * Icon, title and body each get their own line, so no title ever clips.
 */
export function StarterCard({
	Icon,
	title,
	description,
	actionLabel = "Use this",
	onClick,
	disabled = false,
	showPointer = true,
	className,
}: StarterCardProps) {
	return (
		<button
			type="button"
			disabled={disabled}
			onClick={onClick}
			className={cn(
				cardShell,
				"h-full p-4",
				showPointer && !disabled ? "cursor-pointer" : "cursor-default",
				disabled ? undefined : "hover:bg-gray-100 dark:hover:bg-gray-800",
				className,
			)}
		>
			<Icon
				className="mb-3 h-[18px] w-[18px] flex-none transform-gpu text-secondary"
				strokeWidth={1.5}
			/>
			<span className="mb-1 text-[13px] font-medium text-primary">{title}</span>
			<span className="flex-1 text-[12px] leading-relaxed text-tertiary">
				{description}
			</span>
			{actionLabel ? (
				<span className="mt-3 flex items-center gap-1 text-[12px] font-medium text-primary">
					{actionLabel}
					<ArrowRight className="h-3 w-3 flex-none" />
				</span>
			) : null}
		</button>
	)
}

const optionShell = cn(
	"cursor-default rounded-xl outline-none",
	"border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-transparent",
	"hover:bg-gray-100 dark:hover:bg-gray-800",
	"focus-visible:ring-1 focus-visible:ring-blue-500",
	"data-[state=checked]:border-green-500 data-[state=checked]:bg-green-500/10",
	"dark:data-[state=checked]:border-green-500 dark:data-[state=checked]:bg-green-500/10",
)

export function OptionCards<T extends string>({
	name,
	value,
	options,
	onValueChange,
	className,
}: Props<T>) {
	const isDetailed = options.some((option) => option.description)

	return (
		<RadioGroupPrimitive.Root
			name={name}
			value={value}
			onValueChange={(next) => onValueChange(next as T)}
			orientation="horizontal"
			className={cn("grid grid-cols-3 gap-2 sm:grid-cols-5", className)}
		>
			{options.map((option) => (
				<RadioGroupPrimitive.Item
					key={option.value}
					value={option.value}
					aria-label={option.label}
					className={cn(
						optionShell,
						isDetailed
							? "flex h-full flex-col items-start gap-2 p-3 text-left"
							: cn(
									"flex h-[4.75rem] flex-col items-center justify-center gap-2 px-1.5",
									"text-tertiary data-[state=checked]:text-primary",
								),
					)}
				>
					<span className="flex w-full items-center justify-between gap-2">
						<option.Icon
							className={cn(
								"flex-none transform-gpu",
								isDetailed ? "h-[18px] w-[18px] text-secondary" : "h-5 w-5",
								!isDetailed && "mx-auto",
							)}
							strokeWidth={1.5}
						/>
						{option.badge ? (
							<Badge size="sm" variant="light-gray">
								{option.badge}
							</Badge>
						) : null}
					</span>
					<span
						className={cn(
							"font-medium leading-tight",
							isDetailed ? "text-[13px] text-primary" : "text-center text-xs",
						)}
					>
						{option.label}
					</span>
					{option.description ? (
						<span className="text-[11.5px] leading-relaxed text-tertiary">
							{option.description}
						</span>
					) : null}
				</RadioGroupPrimitive.Item>
			))}
		</RadioGroupPrimitive.Root>
	)
}

export interface TriggerActionCardProps {
	Icon: ComponentType<SVGProps<SVGSVGElement>>
	iconClassName?: string
	title: string
	description: string
	TriggerIcon: ComponentType<SVGProps<SVGSVGElement>>
	triggerLabel: string
	ActionIcon: ComponentType<SVGProps<SVGSVGElement>>
	actionLabel: string
	onClick: () => void
	disabled?: boolean
	showPointer?: boolean
	className?: string
}

/**
 * Two layers: an inner panel that pitches the routine, and a frame whose footer
 * reads trigger then action, so the card says what starts it and what it does.
 */
export function TriggerActionCard({
	Icon,
	iconClassName,
	title,
	description,
	TriggerIcon,
	triggerLabel,
	ActionIcon,
	actionLabel,
	onClick,
	disabled = false,
	showPointer = true,
	className,
}: TriggerActionCardProps) {
	return (
		<button
			type="button"
			disabled={disabled}
			onClick={onClick}
			aria-label={`${title}. ${triggerLabel} then ${actionLabel}`}
			className={cn(
				"group flex h-full flex-col rounded-xl p-1 text-left outline-none",
				"border border-gray-200 bg-gray-50 dark:border-gray-700/80 dark:bg-gray-900",
				"focus-visible:ring-1 focus-visible:ring-blue-500",
				showPointer && !disabled ? "cursor-pointer" : "cursor-default",
				disabled ? "opacity-60" : undefined,
				className,
			)}
		>
			<span
				className={cn(
					"flex flex-1 flex-col rounded-[9px] p-3.5",
					"border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-850",
					disabled
						? undefined
						: "group-hover:bg-gray-100 dark:group-hover:bg-gray-800",
				)}
			>
				<Icon
					className={cn(
						"mb-2.5 h-[18px] w-[18px] flex-none transform-gpu",
						iconClassName ?? "text-secondary",
					)}
					strokeWidth={1.5}
				/>
				<span className="text-[13px] font-medium text-primary">{title}</span>
				<span className="mt-1 text-[12px] leading-relaxed text-tertiary">
					{description}
				</span>
			</span>
			<span className="flex items-center gap-1.5 px-2.5 py-2 text-[11.5px] text-tertiary">
				<TriggerIcon className="h-3.5 w-3.5 flex-none" strokeWidth={1.5} />
				<span className="truncate">{triggerLabel}</span>
				<ArrowRight className="h-3 w-3 flex-none" strokeWidth={1.5} />
				<ActionIcon className="h-3.5 w-3.5 flex-none" strokeWidth={1.5} />
				<span className="truncate text-secondary">{actionLabel}</span>
			</span>
		</button>
	)
}
